import { useState } from "react";
import dayjs, { type Dayjs } from "dayjs";
import { Search, ListFilter, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePickerField } from "@/components/ui/date-picker";

function formatType(type: string): string {
  return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export interface FilterValues {
  search: string;
  selectedTypes: string[];
  createdFrom: Dayjs | null;
  createdTo: Dayjs | null;
}

interface TransactionFiltersPanelProps {
  filters: FilterValues;
  onChange: (filters: FilterValues) => void;
  availableTypes: string[];
  hideTypeFilter?: boolean;
  actions?: React.ReactNode;
}

export function getDefaultFilters(): FilterValues {
  return { search: "", selectedTypes: [], createdFrom: null, createdTo: null };
}

export default function TransactionFiltersPanel({
  filters,
  onChange,
  availableTypes,
  hideTypeFilter = false,
  actions,
}: TransactionFiltersPanelProps) {
  const [showFilters, setShowFilters] = useState(false);

  const update = (partial: Partial<FilterValues>) => {
    onChange({ ...filters, ...partial });
  };

  const hasActiveFilters =
    filters.selectedTypes.length > 0 || !!filters.createdFrom || !!filters.createdTo;

  return (
    <div>
      {/* Search bar + filter toggle + actions */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="relative w-[220px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search purpose..."
            value={filters.search}
            onChange={(e) => update({ search: e.target.value })}
            className="pl-8 h-9"
          />
        </div>
        <Button
          variant={showFilters || hasActiveFilters ? "default" : "ghost"}
          size="icon"
          onClick={() => setShowFilters((p) => !p)}
        >
          <ListFilter className="h-4 w-4" />
        </Button>
        {actions}
      </div>

      {/* Collapsible filter panel */}
      <Collapsible open={showFilters}>
        <CollapsibleContent>
          <div className="flex flex-wrap items-end gap-3 mb-4 p-4 rounded-lg bg-muted/50">
            {!hideTypeFilter && (
              <div className="min-w-[220px]">
                <Select
                  value={filters.selectedTypes[0] ?? ""}
                  onValueChange={(val) => update({ selectedTypes: val ? [val] : [] })}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Transaction Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTypes.map((t) => (
                      <SelectItem key={t} value={t}>
                        {formatType(t)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <DatePickerField
              value={filters.createdFrom?.toDate()}
              onChange={(d) => update({ createdFrom: d ? dayjs(d) : null })}
              placeholder="Created From"
              className="w-[160px]"
            />
            <DatePickerField
              value={filters.createdTo?.toDate()}
              onChange={(d) => update({ createdTo: d ? dayjs(d) : null })}
              placeholder="Created To"
              className="w-[160px]"
            />

            {hasActiveFilters && (
              <Badge
                variant="outline"
                className="cursor-pointer gap-1 h-9 px-3"
                onClick={() =>
                  update({ selectedTypes: [], createdFrom: null, createdTo: null })
                }
              >
                Clear Filters
                <X className="h-3 w-3" />
              </Badge>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
